// iter56 · Foundation Slice 1A COMPLETION PATCH — Job Detail page.
// Renders a single Job (from /api/foundation/jobs/{jid}) with its parent
// customer + property, universal Documents list, Photos/Files section, and
// a human-readable Activity timeline.
//
// Read-only lookups only. This page does NOT send any customer messages
// and does NOT share any documents — creating a Job is strictly internal.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Briefcase, Home, User, FileText, Camera, Activity, Lock, Plus, History, ArrowRight } from "lucide-react";
import JobHistoryDrawer from "@/components/JobHistoryDrawer";

// ---------------------------------------------------------------------
// Universal document status labels — matches backend foundation.DOC_STATUSES.
// Keep in sync with `/app/backend/foundation.py :: DOC_STATUSES`.
// ---------------------------------------------------------------------
const STATUS_LABEL = {
  draft: "Draft",
  ready_to_send: "Ready to send",
  sent: "Sent",
  delivered: "Delivered",
  viewed: "Viewed",
  action_required: "Action required",
  revision_requested: "Revision requested",
  approved: "Approved",
  awaiting_signature: "Awaiting signature",
  partially_signed: "Partially signed",
  signed: "Signed",
  fully_executed: "Fully executed",
  declined: "Declined",
  expired: "Expired",
  replaced: "Replaced",
  canceled: "Canceled",
  uploaded: "Uploaded",
  shared: "Shared",
  payment_due: "Payment due",
  partially_paid: "Partially paid",
  paid: "Paid",
};

const ACCESS_LABEL = {
  use_business_default: "Use business default",
  portal_and_email: "Portal + Email",
  email_only: "Email only",
  sms_only: "SMS only",
  no_customer_access: "No customer access",
};

const JOB_TYPE_LABEL = {
  estimate_or_proposal: "Estimate / Proposal",
  inspection: "Inspection",
  service_or_repair: "Service or Repair",
  active_project: "Active Project",
  warranty_or_callback: "Warranty or Callback",
  custom: "Custom",
  not_determined: "Not determined",
};

// Turn an activity `kind` (like "document.status.sent") into a plain
// English phrase for the timeline.
const humanizeActivity = (a) => {
  const kind = a.kind || "";
  const d = a.details || {};
  if (kind === "customer.created") return "Customer added";
  if (kind === "customer.edited") return "Customer updated";
  if (kind === "property.created") return "Property added";
  if (kind === "property.edited") return "Property updated";
  if (kind === "job.created") return "Job created";
  if (kind === "job.edited") return "Job updated";
  if (kind === "document.created") return `Document created${d.doc_type ? ` (${d.doc_type})` : ""}`;
  if (kind === "document.edited") return "Document edited";
  if (kind === "document.replaced") return "New version created";
  if (kind === "estimate.new_version_created") return "New estimate version created";
  if (kind === "estimate.created") return `Estimate created${d.version && d.version > 1 ? ` (v${d.version})` : ""}`;
  if (kind === "proposal.created") return "Proposal created";
  if (kind === "contract.created") return "Contract created";
  if (kind === "invoice.created") return `Invoice created${d.invoice_type ? ` (${(d.invoice_type || "").replace(/_/g, " ")})` : ""}`;
  if (kind === "estimate.new_version_created") return "New estimate version created";
  if (kind.startsWith("document.status.")) {
    const s = kind.replace("document.status.", "");
    return `Document marked ${STATUS_LABEL[s] || s}`;
  }
  if (kind.startsWith("document.visibility.")) {
    const v = kind.replace("document.visibility.", "");
    return `Document visibility set to ${v.replace(/_/g, " ")}`;
  }
  return kind.replace(/\./g, " · ");
};

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

// iter58 · Slice 1C — filter chip strip config. Contractor-friendly
// bucket names + the universal-16 statuses each bucket covers. Keep in
// sync with foundation.get_job_history :: BUCKETS.
const FILTER_BUCKETS = [
  { key: "all", label: "All", statuses: null },
  { key: "draft", label: "Draft", statuses: ["draft", "ready_to_send"] },
  { key: "sent", label: "Sent", statuses: ["sent", "delivered", "viewed"] },
  { key: "action_needed", label: "Action Needed",
    statuses: ["action_required", "revision_requested",
               "awaiting_signature", "partially_signed"] },
  { key: "approved", label: "Approved", statuses: ["approved"] },
  { key: "signed_executed", label: "Signed / Executed",
    statuses: ["signed", "fully_executed"] },
  { key: "payment_due", label: "Payment Due", statuses: ["payment_due"] },
  { key: "paid", label: "Paid", statuses: ["paid", "partially_paid"] },
  { key: "replaced_canceled", label: "Replaced / Canceled",
    statuses: ["replaced", "canceled", "declined", "expired"] },
];

const matchesFilter = (status, filterKey) => {
  if (filterKey === "all") return true;
  const bucket = FILTER_BUCKETS.find((b) => b.key === filterKey);
  return bucket && bucket.statuses && bucket.statuses.includes(status || "");
};

const FilterChips = ({ counts, current, onChange }) => (
  <div className="flex flex-wrap gap-1.5 mb-3" data-testid="documents-filter-chips">
    {FILTER_BUCKETS.map((b) => {
      const count = counts?.[b.key] ?? 0;
      // Only render chips with matching docs, except "All".
      if (b.key !== "all" && count === 0) return null;
      const active = current === b.key;
      return (
        <button
          key={b.key}
          onClick={() => onChange(b.key)}
          data-testid={`filter-chip-${b.key}`}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border ${active ? "bg-[var(--jc-navy)] text-white border-[var(--jc-navy)]" : "bg-white text-[var(--jc-text)] border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"}`}
        >
          {b.label} <span className={active ? "opacity-80" : "text-[var(--jc-text-soft)]"}>· {count}</span>
        </button>
      );
    })}
  </div>
);

const JobDetail = () => {
  const { jid } = useParams();
  const { t } = useLang();
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // iter58 · Slice 1C — filter chip strip + history drawer + latest
  // activity summary. Fetched from a single /jobs/{jid}/history call.
  const [history, setHistory] = useState(null);
  const [filter, setFilter] = useState("all");
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/foundation/jobs/${jid}`);
        if (cancelled) return;
        setJob(data);
        // Fetch customer + property in parallel; ignore individual failures.
        const [c, p] = await Promise.all([
          data.customer_id ? api.get(`/foundation/customers/${data.customer_id}`).then((r) => r.data).catch(() => null) : null,
          data.property_id ? api.get(`/foundation/properties/${data.property_id}`).then((r) => r.data).catch(() => null) : null,
        ]);
        if (!cancelled) { setCustomer(c); setProperty(p); }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jid]);

  // iter58 · Fetch history once for latest-activity summary + filter
  // counts. Also refreshed after any refetch trigger.
  useEffect(() => {
    if (!jid) return;
    api.get(`/foundation/jobs/${jid}/history`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory(null));
  }, [jid]);

  if (loading) return <DashboardLayout title="Job" subtitle="Loading…"><div data-testid="job-detail-loading" /></DashboardLayout>;
  if (notFound || !job) return (
    <DashboardLayout title="Job" subtitle="Not found">
      <div data-testid="job-detail-notfound" className="jc-card p-4">{t("job_detail.not_available")}</div>
    </DashboardLayout>
  );

  const documents = (job.documents || []).filter((d) => (d.doc_type || "") !== "photo");
  const photos = (job.documents || []).filter((d) => (d.doc_type || "") === "photo");
  const activities = job.activities || [];

  const customerName =
    (customer && (customer.business_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim())) ||
    "Customer";
  const propertyLine = property
    ? `${property.nickname || property.street_address || "(no address)"} · ${[property.city, property.state, property.zip_code].filter(Boolean).join(" ")}`
    : "Property";

  return (
    <DashboardLayout
      title={job.name || "Job"}
      subtitle={`${JOB_TYPE_LABEL[job.job_type] || job.job_type || "Job"} · ${STATUS_LABEL[job.status] || job.status || "draft"}`}
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setHistoryOpen(true)}
            data-testid="view-job-history-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white hover:bg-[var(--jc-bg)]"
          >
            <History size={14} /> View Job History
          </button>
          <Link
            data-testid="job-create-estimate-btn"
            to={{
              pathname: "/app/estimates/new",
              search: new URLSearchParams({
                job_id: job.id,
                customer_name: customerName,
                customer_phone: customer?.primary_phone || "",
                customer_email: customer?.primary_email || "",
                job_address: property
                  ? [property.street_address, property.city, property.state, property.zip_code].filter(Boolean).join(", ")
                  : "",
                job_type: job.job_type || "",
              }).toString(),
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md jc-btn-primary"
          >
            <Plus size={14} /> Create estimate
          </Link>
        </div>
      }
    >
      <div data-testid="job-detail-page" className="space-y-4 max-w-4xl">
        {/* iter58 · Slice 1C — Latest Activity summary card. Job-scoped.
            Sibling jobs on the same customer never leak in — backend
            filter is strictly details.job_id == jid. */}
        {history?.latest_activity && (
          <section data-testid="job-latest-activity" className="jc-card p-3 border-l-4 border-l-[var(--jc-navy)]">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-[var(--jc-text-soft)] uppercase tracking-wide">
                  Latest activity
                </div>
                <div className="text-sm font-semibold" data-testid="job-latest-activity-label">
                  {history.latest_activity.label}
                </div>
                {history.latest_activity.at && (
                  <div className="text-xs text-[var(--jc-text-soft)]">
                    {new Date(history.latest_activity.at).toLocaleString("en-US", {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "numeric", minute: "2-digit",
                    })}
                  </div>
                )}
              </div>
              {history.latest_activity.document_id && history.latest_activity.doc_type && ({
                estimate: `/app/estimates/`, proposal: `/app/proposals/`,
                contract: `/app/contracts/`, invoice: `/app/invoices/`,
              }[history.latest_activity.doc_type]) && (
                <Link
                  to={`${({
                    estimate: `/app/estimates/`, proposal: `/app/proposals/`,
                    contract: `/app/contracts/`, invoice: `/app/invoices/`,
                  })[history.latest_activity.doc_type]}${history.latest_activity.document_id}`}
                  data-testid="job-latest-activity-open"
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold underline text-[var(--jc-navy)]"
                >
                  Open <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </section>
        )}
        {/* Parent record summary */}
        <section data-testid="job-parent-summary" className="jc-card p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-start gap-2">
            <User size={14} className="mt-0.5 text-[var(--jc-text-soft)]" />
            <div>
              <div className="text-xs text-[var(--jc-text-soft)]">{t("job_detail.customer")}</div>
              {customer ? (
                <Link
                  data-testid="job-customer-link"
                  to={`/app/customers/${customer.id}`}
                  className="text-sm font-semibold underline"
                >{customerName}</Link>
              ) : (
                <span className="text-sm font-semibold" data-testid="job-customer-name">{customerName}</span>
              )}
              {(customer?.primary_phone || customer?.primary_email) && (
                <div className="text-xs text-[var(--jc-text-soft)]">
                  {[customer.primary_phone, customer.primary_email].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Home size={14} className="mt-0.5 text-[var(--jc-text-soft)]" />
            <div>
              <div className="text-xs text-[var(--jc-text-soft)]">{t("job_detail.property")}</div>
              <div className="text-sm font-semibold" data-testid="job-property-line">{propertyLine}</div>
              {property?.access_instructions && (
                <div className="text-xs text-[var(--jc-text-soft)]" data-testid="job-property-access">
                  Access (internal): {property.access_instructions}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Job details */}
        <section data-testid="job-details" className="jc-card p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Briefcase size={14} /> Job details
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-xs text-[var(--jc-text-soft)]">{t("job_detail.type")}</dt>
              <dd data-testid="job-type-value">{JOB_TYPE_LABEL[job.job_type] || job.job_type || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--jc-text-soft)]">{t("job_detail.status")}</dt>
              <dd data-testid="job-status-value">{STATUS_LABEL[job.status] || job.status || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--jc-text-soft)]">Trade</dt>
              <dd data-testid="job-trade-value">{job.trade || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--jc-text-soft)] flex items-center gap-1"><Lock size={10} /> Customer access preference</dt>
              <dd data-testid="job-customer-access-value">{ACCESS_LABEL[job.customer_access_preference] || job.customer_access_preference || "Use business default"}</dd>
            </div>
            {job.description && (
              <div className="md:col-span-2">
                <dt className="text-xs text-[var(--jc-text-soft)]">Description (internal)</dt>
                <dd className="whitespace-pre-wrap" data-testid="job-description-value">{job.description}</dd>
              </div>
            )}
            {job.internal_notes && (
              <div className="md:col-span-2">
                <dt className="text-xs text-[var(--jc-text-soft)]">Internal notes</dt>
                <dd className="whitespace-pre-wrap" data-testid="job-internal-notes-value">{job.internal_notes}</dd>
              </div>
            )}
          </dl>
        </section>

        {/* Documents */}
        <section data-testid="job-documents" className="jc-card p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileText size={14} /> Documents ({documents.length})
            </div>
          </div>
          {/* iter58 · Slice 1C — filter chip strip. Contractor-friendly
              buckets; client-side filter only, no backend state change.
              Only chips with matching documents are shown. */}
          {history?.filter_counts && (
            <FilterChips
              counts={history.filter_counts}
              current={filter}
              onChange={setFilter}
            />
          )}
          <ul className="space-y-1.5" data-testid="job-documents-list">
            {documents
              .filter((d) => matchesFilter(d.status, filter))
              .map((d) => {
              const routeByType = {
                estimate: `/app/estimates/${d.source_record_id || d.id}`,
                proposal: `/app/proposals/${d.source_record_id || d.id}`,
                contract: `/app/contracts/${d.source_record_id || d.id}`,
                invoice: `/app/invoices/${d.source_record_id || d.id}`,
              };
              const openHref = routeByType[d.doc_type];
              const lastActive = d.updated_at || d.sent_at || d.approved_at || d.signed_at || d.created_at;
              return (
                <li key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <span className="font-medium capitalize">{d.doc_type?.replace(/_/g, " ")}</span>
                    <span className="text-xs text-[var(--jc-text-soft)]"> · v{d.version || 1} · {d.doc_number || "no #"}</span>
                    <span className="text-xs text-[var(--jc-text-soft)]"> · {(d.visibility || "internal_only") === "internal_only" ? "Internal only" : "Customer visible"}</span>
                    {lastActive && (
                      <div className="text-[11px] text-[var(--jc-text-soft)]">last activity {formatWhen(lastActive)}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--jc-text-soft)]">{STATUS_LABEL[d.status] || d.status}</span>
                    {openHref && (
                      <Link
                        to={openHref}
                        data-testid={`document-open-${d.id}`}
                        className="text-xs font-semibold underline text-[var(--jc-navy)]"
                      >Open</Link>
                    )}
                  </div>
                </li>
              );
            })}
            {documents.filter((d) => matchesFilter(d.status, filter)).length === 0 && (
              <li className="text-xs text-[var(--jc-text-soft)]" data-testid="documents-empty">
                {filter === "all"
                  ? "No documents yet. Estimates, proposals, contracts, and invoices attached to this job will appear here."
                  : "No documents in this filter."}
              </li>
            )}
          </ul>
          <div className="mt-2">
            <button
              onClick={() => setHistoryOpen(true)}
              data-testid="documents-view-history-btn"
              className="text-xs font-semibold underline text-[var(--jc-navy)]"
            >
              View History
            </button>
          </div>
        </section>

        {/* Photos / Files */}
        <section data-testid="job-photos" className="jc-card p-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
            <Camera size={14} /> Photos &amp; Files ({photos.length})
          </div>
          <ul className="space-y-1.5" data-testid="job-photos-list">
            {photos.map((p) => (
              <li key={p.id} data-testid={`photo-row-${p.id}`} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">{p.doc_number || "Photo"}</span>
                  <span className="text-xs text-[var(--jc-text-soft)]"> · v{p.version || 1}</span>
                </div>
                <span className="text-xs text-[var(--jc-text-soft)]">{STATUS_LABEL[p.status] || p.status}</span>
              </li>
            ))}
            {photos.length === 0 && (
              <li className="text-xs text-[var(--jc-text-soft)]" data-testid="photos-empty">
                No photos or files yet.
              </li>
            )}
          </ul>
        </section>

        {/* Activity timeline */}
        <section data-testid="job-activity" className="jc-card p-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
            <Activity size={14} /> Activity
          </div>
          <ol className="space-y-2 border-l border-[var(--jc-border)] pl-3" data-testid="job-activity-list">
            {activities.map((a) => (
              <li key={a.id} className="relative text-sm" data-testid={`activity-row-${a.id}`}>
                <span className="absolute -left-[7px] top-1.5 w-2 h-2 rounded-full bg-[var(--jc-navy)]" />
                <div className="font-medium">{humanizeActivity(a)}</div>
                <div className="text-xs text-[var(--jc-text-soft)]">{formatWhen(a.at)}</div>
              </li>
            ))}
            {activities.length === 0 && (
              <li className="text-xs text-[var(--jc-text-soft)]" data-testid="activity-empty">
                No activity yet.
              </li>
            )}
          </ol>
        </section>
      </div>
      {/* iter58 · Slice 1C — Job History drawer. Loads on demand. */}
      <JobHistoryDrawer open={historyOpen} onOpenChange={setHistoryOpen} jobId={jid} />
    </DashboardLayout>
  );
};

export default JobDetail;
